import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import useKeyActivityState, { IKeyActivity } from '@/states/key-activities.state';
import useTenantState from '@/states/tenant.state';
import useOrgChartState from '@/states/organization.state';
import { useRefetchCompletionStatus } from './useCompletionStatus';

export interface InlineRow {
  tempId: string;
  org_chart_node_id: string;
  ka1: string;
  ka2: string;
  ka3: string;
  ka4: string;
}

/** Keadaan server saat fetch: seluruh record KA per jabatan (id naik) dan jabatan yang tampil di tabel. */
interface Snapshot {
  shownNodeIds: Set<number>;
  recordsByNode: Map<number, IKeyActivity[]>;
}

type SaveOp =
  | { kind: 'delete'; id: number | string }
  | { kind: 'update'; id: number | string; payload: Partial<IKeyActivity> }
  | { kind: 'create'; payload: Partial<IKeyActivity> };

const KA_PAGE_SIZE = 100;
const KA_SLOTS = 4;
const silentOpts = { silent: true };

function buildSnapshot(groupedNodes: IKeyActivity[], allRecords: IKeyActivity[]): Snapshot {
  const recordsByNode = new Map<number, IKeyActivity[]>();
  [...allRecords]
    .sort((a, b) => Number(a.id) - Number(b.id))
    .forEach(ka => {
      if (ka.org_chart_node_id == null) return;
      const nodeId = Number(ka.org_chart_node_id);
      const list = recordsByNode.get(nodeId);
      if (list) list.push(ka);
      else recordsByNode.set(nodeId, [ka]);
    });
  const shownNodeIds = new Set(
    groupedNodes.filter(g => g.org_chart_node_id != null).map(g => Number(g.org_chart_node_id)),
  );
  return { shownNodeIds, recordsByNode };
}

/** Slot KA1..KA4 sebuah jabatan = 4 record pertamanya; tiap sel dibandingkan dengan record slot itu sendiri. */
function planRowOps(row: InlineRow, records: IKeyActivity[]): SaveOp[] {
  const nodeId = Number(row.org_chart_node_id);
  const ops: SaveOp[] = [];
  [row.ka1, row.ka2, row.ka3, row.ka4].forEach((cell, i) => {
    const val = cell.trim();
    const record = i < KA_SLOTS ? records[i] : undefined;
    if (!record) {
      if (val) ops.push({ kind: 'create', payload: { name: val, org_chart_node_id: nodeId, is_published: false } });
    } else if (!val) {
      ops.push({ kind: 'delete', id: record.id });
    } else if (val !== record.name?.trim()) {
      const payload: Partial<IKeyActivity> = { name: val, org_chart_node_id: nodeId };
      if (record.is_published !== undefined) payload.is_published = record.is_published;
      ops.push({ kind: 'update', id: record.id, payload });
    }
  });
  return ops;
}

/** Jabatan yang tampil tetapi tak dipegang baris valid mana pun dihapus seluruh record-nya; jabatan yang tak tampil tak disentuh. */
function planSaveOps(validRows: InlineRow[], snapshot: Snapshot): SaveOp[] {
  const heldNodeIds = new Set(validRows.map(r => Number(r.org_chart_node_id)));
  const ops: SaveOp[] = [];
  snapshot.shownNodeIds.forEach(nodeId => {
    if (heldNodeIds.has(nodeId)) return;
    (snapshot.recordsByNode.get(nodeId) ?? []).forEach(ka => ops.push({ kind: 'delete', id: ka.id }));
  });
  validRows.forEach(row =>
    ops.push(...planRowOps(row, snapshot.recordsByNode.get(Number(row.org_chart_node_id)) ?? [])),
  );
  return ops;
}

function findDuplicateNodeId(validRows: InlineRow[]): number | null {
  const seen = new Set<number>();
  for (const row of validRows) {
    const nodeId = Number(row.org_chart_node_id);
    if (seen.has(nodeId)) return nodeId;
    seen.add(nodeId);
  }
  return null;
}

let _rowCounter = 0;
const newTempId = () => `row-${++_rowCounter}`;

export default function useKeyActivityHandler() {
  const refetchCompletionStatus = useRefetchCompletionStatus();
  const { currentTenant } = useTenantState();
  const { orgCharts, fetchOrgChartTree } = useOrgChartState();

  const {
    getKeyActivities,
    getGroupedKeyActivities,
    setKeyActivities,
    createKeyActivity,
    updateKeyActivity,
    deleteKeyActivity,
    isLoading,
  } = useKeyActivityState();

  const [rows, setRows] = useState<InlineRow[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showPanel, setShowPanel] = useState(true);
  const [fetchSeq, setFetchSeq] = useState(0);
  const [dataReady, setDataReady] = useState(false);
  const snapshot = useRef<Snapshot | null>(null);

  const flattenTree = (nodes: { id: number; name: string; children?: unknown[] }[]): { id: number; name: string }[] => {
    const result: { id: number; name: string }[] = [];
    nodes.forEach(node => {
      result.push({ id: node.id, name: node.name });
      if (node.children?.length) {
        result.push(...flattenTree(node.children as { id: number; name: string; children?: unknown[] }[]));
      }
    });
    return result;
  };

  const allRoles = useMemo(
    () => flattenTree(orgCharts).sort((a, b) => a.id - b.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orgCharts],
  );

  const fetchData = useCallback(async () => {
    if (!currentTenant?.id) return;
    try {
      await fetchOrgChartTree(currentTenant.id);
      // Semua data dikumpulkan ke variabel lokal; snapshot, store, dan baris tabel baru diperbarui setelah semuanya sukses.
      const grouped = await getGroupedKeyActivities(currentTenant.id, {
        page: 0,
        page_size: 200,
        order_by: 'created_at',
        order: 'asc',
      });
      if (!grouped) throw new Error('Gagal mengambil jabatan key activity');

      const kaParams = { page_size: KA_PAGE_SIZE, order: 'asc', order_by: 'id' } as const;
      const firstPage = await getKeyActivities(currentTenant.id, { ...kaParams, page: 0 });
      if (!firstPage) throw new Error('Gagal mengambil key activity');
      const all = [...firstPage.content];
      for (let page = 1; page < firstPage.totalPages; page++) {
        const next = await getKeyActivities(currentTenant.id, { ...kaParams, page });
        if (!next) throw new Error('Gagal mengambil halaman key activity');
        all.push(...next.content);
      }

      if (firstPage.totalPages > 1) setKeyActivities(all);
      snapshot.current = buildSnapshot(grouped.content, all);
      setDataReady(true);
      _rowCounter = 0;
      setFetchSeq(n => n + 1);
    } catch {
      snapshot.current = null;
      setDataReady(false);
      toast.error('Gagal mengambil data');
    }
  }, [currentTenant?.id, fetchOrgChartTree, getGroupedKeyActivities, getKeyActivities, setKeyActivities]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveAndNext = async (onSuccess?: () => void) => {
    if (!currentTenant?.id) return;

    if (!snapshot.current) {
      toast.error('Data belum termuat, muat ulang halaman');
      return;
    }

    const validRows = rows.filter(r => r.org_chart_node_id && [r.ka1, r.ka2, r.ka3, r.ka4].some(cell => cell.trim()));
    if (validRows.length === 0) {
      toast.error('Isi minimal satu jabatan dengan minimal satu key activity');
      return;
    }

    const duplicateNodeId = findDuplicateNodeId(validRows);
    if (duplicateNodeId !== null) {
      const name = allRoles.find(role => role.id === duplicateNodeId)?.name ?? duplicateNodeId;
      toast.error(`Jabatan ${name} dipilih lebih dari sekali`);
      return;
    }

    setIsSaving(true);
    try {
      const tenantId = currentTenant.id;
      for (const op of planSaveOps(validRows, snapshot.current)) {
        if (op.kind === 'delete') await deleteKeyActivity(tenantId, op.id, silentOpts);
        else if (op.kind === 'update') await updateKeyActivity(tenantId, op.id, op.payload, silentOpts);
        else await createKeyActivity(tenantId, op.payload, silentOpts);
      }

      await refetchCompletionStatus();
      toast.success('Key activities berhasil disimpan');
      onSuccess?.();
    } catch {
      toast.error('Gagal menyimpan key activities');
      // Sebagian operasi mungkin sudah terkirim; tarik ulang agar id per sel kembali sinkron dengan server.
      await fetchData();
    } finally {
      setIsSaving(false);
    }
  };

  const updateRow = (tempId: string, patch: Partial<InlineRow>) =>
    setRows(prev => prev.map(r => (r.tempId === tempId ? { ...r, ...patch } : r)));

  const addRow = () =>
    setRows(prev => [
      ...prev,
      {
        tempId: newTempId(),
        org_chart_node_id: '',
        ka1: '',
        ka2: '',
        ka3: '',
        ka4: '',
      },
    ]);

  const removeRow = (tempId: string) => setRows(prev => prev.filter(r => r.tempId !== tempId));

  return {
    rows,
    setRows,
    isSaving,
    dataReady,
    showPanel,
    setShowPanel,
    fetchSeq,
    isLoading,
    allRoles,
    newTempId,
    updateRow,
    addRow,
    removeRow,
    handleSaveAndNext,
  };
}
